"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TwilioRoom = void 0;
const analytics_1 = require("@streem/analytics");
const logger_1 = require("@streem/logger");
const sdk_lightshow_1 = require("@streem/sdk-lightshow");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const twilio_video_1 = __importDefault(require("twilio-video"));
const logger_2 = require("../util/logger");
const media_1 = require("../util/media");
const audio_tracks_1 = require("./tracks/audio_tracks");
const data_tracks_1 = require("./tracks/data_tracks");
const video_tracks_1 = require("./tracks/video_tracks");
const room_monitor_1 = require("./room_monitor");
class TwilioRoom {
    constructor(config) {
        this.config = config;
        this.connectionStatusSubject = new rxjs_1.BehaviorSubject('DISCONNECTED');
        this.errorCodeSubject = new rxjs_1.BehaviorSubject(undefined);
        this.recordingSubject = new rxjs_1.BehaviorSubject(false);
        // region local subjects
        this.localConnectionSubject = new rxjs_1.BehaviorSubject(false);
        this.localNetworkSubject = new rxjs_1.BehaviorSubject(0);
        this.localAudioSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localAudioErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localVideoSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localVideoErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localDataSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localDataErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        // endregion
        // region remote subjects
        this.remoteConnectionSubject = new rxjs_1.BehaviorSubject(false);
        this.remoteNetworkSubject = new rxjs_1.BehaviorSubject(0);
        this.remoteAudioSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteAudioErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteVideoSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteVideoErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteDataSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteDataErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        // endregion
        this.mediaWebRtcStatsSubject = new rxjs_1.Subject();
        this.mediaWebRtcStats = this.mediaWebRtcStatsSubject.asObservable();
        this.isLocalVideoFrozenSubject = new rxjs_1.Subject();
        this.isLocalVideoFrozen = this.isLocalVideoFrozenSubject.asObservable();
        this.roomMonitorStatsSubject = new rxjs_1.Subject();
        this.roomMonitorStats = this.roomMonitorStatsSubject.asObservable();
        this.localTracks = [];
        this.connectionState = this.connectionStatusSubject.asObservable();
        this.errorCode = this.errorCodeSubject.pipe((0, operators_1.skip)(1));
        this.recording = this.recordingSubject.asObservable();
        // region local observables
        this.localNetworkQuality = this.localNetworkSubject.asObservable();
        this.localConnection = this.localConnectionSubject.asObservable();
        this.localAudioTrack = this.localAudioSubject.asObservable();
        this.localAudioErrorCode = this.localAudioErrorSubject.asObservable();
        this.localVideoTrack = this.localVideoSubject.asObservable();
        this.localVideoErrorCode = this.localVideoErrorSubject.asObservable();
        this.localDataTrack = this.localDataSubject.asObservable();
        this.localDataErrorCode = this.localDataErrorSubject.asObservable();
        // endregion
        // region remote observables
        this.remoteNetworkQuality = this.remoteNetworkSubject.asObservable();
        this.remoteConnection = this.remoteConnectionSubject.asObservable();
        this.remoteAudioTrack = this.remoteAudioSubject.asObservable();
        this.remoteAudioErrorCode = this.remoteAudioErrorSubject.asObservable();
        this.remoteVideoTrack = this.remoteVideoSubject.asObservable();
        this.remoteVideoErrorCode = this.remoteVideoErrorSubject.asObservable();
        this.remoteDataTrack = this.remoteDataSubject.asObservable();
        this.remoteDataErrorCode = this.remoteDataErrorSubject.asObservable();
        // endregion
        this._localAudioTrackEnabled = false;
        this._localVideoTrackEnabled = false;
        // user media request error observables
        this.getUserMediaVideoErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.getUserMediaVideoError = this.getUserMediaVideoErrorSubject.asObservable();
        this.getUserMediaAudioErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.getUserMediaAudioError = this.getUserMediaAudioErrorSubject.asObservable();
        // If/When we are ready to run Lightshow V2 on Twilio, we can proxy the Remote Capabilities into the LightshowEngine,
        // and watch for upgrades to V2.  Note that we'd also need to declare our own capability of supporting V2 in room_controller.ts.
        // For now, since we added V2 support as part of ARaaS, leave the Twilio code alone... -SA
        this.lightshowEngine = new sdk_lightshow_1.LightshowEngine(undefined);
        this.localDevicesSubject = new rxjs_1.BehaviorSubject([]);
        this.localDevices = this.localDevicesSubject.asObservable();
        this.trackProviderName = 'Twilio';
        this.useNamedVideoTracks = true;
        this.deviceChangeListener = () => __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.getMediaDevices)().then(devices => {
                logger_2.logger.setContextValue(logger_1.contextValues.AVAILABLE_MEDIA_DEVICES, devices);
                logger_2.logger.info('Available media devices updated');
                logger_2.logger.removeContextValue(logger_1.contextValues.AVAILABLE_MEDIA_DEVICES);
                this.localDevicesSubject.next(devices);
            });
        });
        this._createLocalVideoTrack = (config) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            let track;
            try {
                track = yield twilio_video_1.default.createLocalVideoTrack(Object.assign({ name: (_a = config.video) === null || _a === void 0 ? void 0 : _a.name }, config.constraints.video));
                track.on('enabled', () => {
                    this._localVideoTrackEnabled = true;
                });
                track.on('disabled', () => {
                    this._localVideoTrackEnabled = false;
                });
                track.enable(config.video.active);
            }
            catch (error) {
                if (error instanceof Error) {
                    if (error.name === 'NotAllowedError') {
                        logger_2.logger.error('Failed to create local video track for twilio. Browser denied device access: ', error);
                        this.getUserMediaVideoErrorSubject.next(error.name);
                    }
                    else {
                        logger_2.logger.error('Failed to create local video track for twilio ', error);
                        this.getUserMediaVideoErrorSubject.next(error.name);
                    }
                }
                return false;
            }
            if (track) {
                this.pushLocalTrack(track);
                return true;
            }
        });
        this._createLocalAudioTrack = (config) => __awaiter(this, void 0, void 0, function* () {
            var _b;
            let track;
            try {
                track = yield twilio_video_1.default.createLocalAudioTrack(Object.assign({ name: (_b = config.audio) === null || _b === void 0 ? void 0 : _b.name }, config.constraints.audio));
                logger_2.logger.info('audioTrackEvent=permission_granted');
                track.on('enabled', () => {
                    this._localAudioTrackEnabled = true;
                });
                track.on('disabled', () => {
                    this._localAudioTrackEnabled = false;
                });
                this._localAudioTrackEnabled = true;
            }
            catch (error) {
                if (error instanceof Error) {
                    if (error.name === 'NotAllowedError') {
                        logger_2.logger.error('Failed to create local audio track for twilio. Browser denied device access: ', error);
                        logger_2.logger.info('audioTrackEvent=permission_denied');
                        this.getUserMediaAudioErrorSubject.next(error.name);
                    }
                    else {
                        logger_2.logger.error('Twilio audio track creation failed: ', error);
                        this.getUserMediaAudioErrorSubject.next(error.name);
                    }
                }
                return false;
            }
            if (track) {
                this.pushLocalTrack(track);
                return true;
            }
        });
        this.pushLocalTrack = (track) => {
            // TODO publish the track if already connected
            const existingIndex = this.localTracks.findIndex(existingTrack => existingTrack.kind === track.kind);
            if (existingIndex > -1) {
                logger_2.logger.warn(`Attempting to create a ${track.kind} track when one already exists, overwriting existing track`);
                this.localTracks.splice(existingIndex, 1, track);
                logger_2.logger.info('audioTrackEvent=replaced');
            }
            else {
                this.localTracks.push(track);
            }
            switch (track.kind) {
                case 'audio':
                    this.localAudioSubject.next(new audio_tracks_1.TwilioLocalAudioTrack(track));
                    logger_2.logger.info('Local audio track created', track);
                    break;
                case 'video':
                    this.localVideoSubject.next(new video_tracks_1.TwilioLocalVideoTrack(track));
                    logger_2.logger.info('Local video track created', track);
                    break;
                case 'data':
                    this.localDataSubject.next(new data_tracks_1.TwilioLocalDataTrack(track));
                    logger_2.logger.info('Local data track created', track);
                    break;
            }
        };
        this.listenToLocalParticipant = () => {
            var _a, _b, _c;
            (_a = this.room) === null || _a === void 0 ? void 0 : _a.localParticipant.on('disconnected', () => {
                logger_2.logger.info('Local participant has disconnected');
                this.localConnectionSubject.next(false);
            });
            (_b = this.room) === null || _b === void 0 ? void 0 : _b.localParticipant.on('networkQualityLevelChanged', (level, networkQualityStats) => {
                logger_2.logger.setContextValue(logger_1.contextValues.TWILIO_NETWORK_QUAL_STATS, networkQualityStats);
                logger_2.logger.info('Local participant network quality has changed to', level);
                logger_2.logger.removeContextValue(logger_1.contextValues.TWILIO_NETWORK_QUAL_STATS);
                this.localNetworkSubject.next(level);
            });
            (_c = this.room) === null || _c === void 0 ? void 0 : _c.localParticipant.on('trackPublicationFailed', (error, localTrack) => {
                const { code } = error;
                logger_2.logger.error(`Local track publication failed for ${localTrack.kind}`, error);
                switch (localTrack.kind) {
                    case 'audio':
                        return this.localAudioErrorSubject.next(code);
                    case 'video':
                        return this.localVideoErrorSubject.next(code);
                    case 'data':
                        return this.localDataErrorSubject.next(code);
                }
            });
        };
        this.onParticipantConnected = (participant) => {
            // Note we are only supporting a single remote participant, this code will need to be updated when we
            // support more than a single expert in a room
            logger_2.logger.info(`Participant ${participant.identity} joined the Twilio room: ${this.roomId}`);
            this.remoteConnectionSubject.next(true);
            participant.on('disconnected', () => {
                logger_2.logger.info('Remote participant disconnected from TwilioRoom');
                this.remoteConnectionSubject.next(false);
            });
            participant.on('networkQualityLevelChanged', (level, networkQualityStats) => {
                logger_2.logger.setContextValue(logger_1.contextValues.TWILIO_NETWORK_QUAL_STATS, networkQualityStats);
                logger_2.logger.info(`Remote participant network quality has changed to ${level}`);
                logger_2.logger.removeContextValue(logger_1.contextValues.TWILIO_NETWORK_QUAL_STATS);
                this.remoteNetworkSubject.next(level);
            });
            participant.on('trackSubscriptionFailed', (error, publication) => {
                const { code } = error;
                logger_2.logger.error(`Failed to subscribe to remote ${publication.kind} track`, code);
                switch (publication.kind) {
                    case 'audio':
                        return this.remoteAudioErrorSubject.next(code);
                    case 'video':
                        return this.remoteVideoErrorSubject.next(code);
                    case 'data':
                        return this.remoteDataErrorSubject.next(code);
                }
            });
            participant.on('trackSubscribed', (track) => {
                // Only a spectator session will need the lightshow engine on the remote video track.
                const lightshowEngineCheck = this.isHostSession
                    ? null
                    : this.lightshowEngine;
                logger_2.logger.info(`Successfully subscribed to remote ${track.kind} track`, track);
                switch (track.kind) {
                    case 'audio':
                        return this.remoteAudioSubject.next(new audio_tracks_1.TwilioRemoteAudioTrack(track));
                    case 'video':
                        return this.remoteVideoSubject.next(new video_tracks_1.TwilioRemoteVideoTrack(track, lightshowEngineCheck));
                    case 'data': {
                        const remoteDataTrack = new data_tracks_1.TwilioRemoteDataTrack(track);
                        this.lightshowEngine.connect(remoteDataTrack.messages);
                        return this.remoteDataSubject.next(remoteDataTrack);
                    }
                }
            });
            participant.on('trackUnsubscribed', (track) => {
                logger_2.logger.info(`Unsubscribed from remote ${track.kind} track`, track);
                switch (track.kind) {
                    case 'audio':
                        return this.remoteAudioSubject.next(undefined);
                    case 'video':
                        return this.remoteVideoSubject.next(undefined);
                    case 'data':
                        return this.remoteDataSubject.next(undefined);
                }
            });
        };
        this.remoteCapabilitiesListener = this.config.remoteParticipantCapabilities.subscribe(capabilities => {
            var _a;
            this.canReceiveVideo = (_a = capabilities === null || capabilities === void 0 ? void 0 : capabilities.canReceiveVideo) !== null && _a !== void 0 ? _a : true;
        });
        this.isHostSession = config.isHostSession;
    }
    // TODO Pass a refreshToken fn like twilio wall sync
    connect(roomId, _) {
        return __awaiter(this, void 0, void 0, function* () {
            this.roomId = roomId;
            if (!this.localTracks.some(track => track.kind === 'video')) {
                logger_2.logger.info('Local User Connecting without local video track');
            }
            if (!this.localTracks.some(track => track.kind === 'audio')) {
                logger_2.logger.warn('Local User Connecting without local audio track');
            }
            if (!this.localTracks.some(track => track.kind === 'data')) {
                logger_2.logger.warn('Local User Connecting without local data track');
            }
            const opts = {
                name: roomId.toString(),
                networkQuality: {
                    local: 3,
                    remote: 3,
                },
                tracks: this.localTracks,
            };
            const availableMediaDevices = yield (0, toolbox_1.getMediaDevices)();
            this.localDevicesSubject.next(availableMediaDevices);
            logger_2.logger.setContextValue(logger_1.contextValues.AVAILABLE_MEDIA_DEVICES, availableMediaDevices);
            // Safari on desktop and iOS does not identify a 'default' device.
            // This variable  will always resolve to 'undefined', but we can still see all available devices in the context log
            // See https://streempro.atlassian.net/browse/ST-1018 for more information
            const defaultMediaDevices = availableMediaDevices.filter(d => d.deviceId === 'default');
            logger_2.logger.info('Room joined with default media devices: ', defaultMediaDevices);
            logger_2.logger.removeContextValue(logger_1.contextValues.AVAILABLE_MEDIA_DEVICES);
            navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeListener);
            try {
                this.room = yield twilio_video_1.default.connect(this.config.roomVideoCredentials, opts);
            }
            catch (error) {
                logger_2.logger.error(`Twilio room failed to connect to room with roomId: ${this.roomId} for userId: ${_}`, error);
                throw error;
            }
            // Twilio doesn't emit a connected event for the local participant so we assume we are connected
            // if we didn't get any errors
            this.roomMonitor = new room_monitor_1.RoomMonitor(this.room);
            this.roomMonitor.roomMonitorStats$.subscribe();
            this.roomMonitor.roomWebRtcStats$.subscribe(stats => this.mediaWebRtcStatsSubject.next(stats));
            this.roomMonitor.isLocalVideoFrozen$.subscribe(isFrozen => this.isLocalVideoFrozenSubject.next(isFrozen));
            this.roomMonitor.roomMonitorStats$.subscribe(stats => this.roomMonitorStatsSubject.next(stats));
            this.analyticsSubscription = (0, rxjs_1.interval)(5000)
                .pipe((0, operators_1.withLatestFrom)(this.roomMonitor.roomWebRtcStats$, this.roomMonitor.roomMonitorStats$))
                .subscribe(([_, webRtcStats, roomMonitorStats]) => {
                this.logStats(webRtcStats, roomMonitorStats);
            });
            this.localConnectionSubject.next(true);
            this.connectionStatusSubject.next('CONNECTED');
            logger_2.logger.info('Connected to room: ', roomId.toString());
            this.listenForEvents();
            // Logging
            (0, media_1.getPermissionsState)()
                .then(permissionsState => {
                logger_2.logger.setContextValue(logger_1.contextValues.PERMISSIONS_STATE, permissionsState);
            })
                .catch(() => { });
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.room) {
                logger_2.logger.warn(`Cannot disconnect from room ${this.roomId} because it is not connected`);
                return;
            }
            yield this.room.disconnect();
            this.lightshowEngine.disconnect();
            this.resetState();
        });
    }
    createMediaStream(config) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check for Microphone and Camera Inputs
            try {
                const hasMic = yield (0, media_1.hasMicrophone)();
                const hasCam = yield (0, media_1.hasCamera)();
                if (!hasMic) {
                    logger_2.logger.warn('User does not have a microphone available. Continuing without creating local audio track.');
                }
                if (!hasCam) {
                    logger_2.logger.warn('User does not have a camera available. Continuing without creating local video track.');
                }
            }
            catch (error) {
                logger_2.logger.error('Failed to detect microphone and/or camera input: ', error);
            }
            // Create Audio Track
            let audioTrackSuccess = false;
            if (config.constraints.audio) {
                try {
                    audioTrackSuccess = yield (0, analytics_1.attemptPermissionRequest)('MICROPHONE', () => this._createLocalAudioTrack(config), error => logger_2.logger.error('Error while initializing local audio track for twilio: ', error));
                }
                catch (error) {
                    logger_2.logger.error('Error with analytics performing their permission request: ', error);
                }
            }
            // Create Video Track
            let videoTrackSuccess = false;
            // If this is a host session we should always create a local video track. If it is a spectator session we only want to create a local track if the remote client can receive video.
            if (config.constraints.video && (this.isHostSession || this.canReceiveVideo)) {
                try {
                    videoTrackSuccess = yield (0, analytics_1.attemptPermissionRequest)('CAMERA', () => this._createLocalVideoTrack(config), error => logger_2.logger.error('Error while initializing local video track for twilio: ', error));
                }
                catch (error) {
                    // TODO indicate to user that track creation was not successful
                    // https://streempro.atlassian.net/browse/STREEM-8679
                    logger_2.logger.error('Error with analytics performing their permission request: ', error);
                }
            }
            // Create Data Track
            let dataTrackSuccess = false;
            try {
                const track = yield new twilio_video_1.default.LocalDataTrack();
                this.pushLocalTrack(track);
                dataTrackSuccess = true;
            }
            catch (error) {
                logger_2.logger.error('Error creating local data track: ', error);
            }
            return {
                audio: audioTrackSuccess,
                video: videoTrackSuccess,
                data: dataTrackSuccess,
            };
        });
    }
    listenForEvents() {
        if (!this.room) {
            logger_2.logger.warn(`Cannot listen to Twilio room ${this.roomId}, no room created`);
            return;
        }
        this.listenForRoomDisconnect();
        this.listenForRoomReconnect();
        this.listenForRecordingEvents();
        this.listenToLocalParticipant();
        this.room.participants.forEach(this.onParticipantConnected);
        this.room.on('participantConnected', this.onParticipantConnected);
    }
    listenForRecordingEvents() {
        var _a, _b;
        (_a = this.room) === null || _a === void 0 ? void 0 : _a.on('recordingStarted', () => {
            logger_2.logger.info('Recording started');
            this.recordingSubject.next(true);
        });
        (_b = this.room) === null || _b === void 0 ? void 0 : _b.on('recordingStopped', () => {
            logger_2.logger.info('Recording stopped');
            this.recordingSubject.next(false);
        });
    }
    listenForRoomDisconnect() {
        var _a;
        (_a = this.room) === null || _a === void 0 ? void 0 : _a.on('disconnected', (info, error) => {
            const errMsg = `The local user has been disconnected from room ${info.name}. `;
            if (error) {
                error.code && this.errorCodeSubject.next(error.code);
                if (error.code === 20104) {
                    logger_2.logger.error(`${errMsg} Signaling reconnection failed due to expired AccessToken!: ${error.message}`);
                }
                else if (error.code === 53000) {
                    logger_2.logger.error(`${errMsg} Signaling reconnection attempts exhausted!: ${error.message}`);
                }
                else if (error.code === 53204) {
                    logger_2.logger.error(`${errMsg} Signaling reconnection took too long! ${error.message}`);
                }
                else if (error.code === 53118) {
                    logger_2.logger.info(`${errMsg} Disconnected from room because room is complete: ${error.message}`);
                }
                else {
                    logger_2.logger.error(`${errMsg} Unexpected disconnected from Twilio room: ${this.roomId}`, error);
                }
            }
            // Stop all tracks except data
            this.localTracks.forEach((track) => {
                // TODO we probably want to add a completed observable
                // that subscribers can use for clean up
                if (track.kind === 'data') {
                    return;
                }
                track.stop();
            });
            logger_2.logger.info('The room has been disconnected');
            this.connectionStatusSubject.next('DISCONNECTED');
        });
    }
    listenForRoomReconnect() {
        var _a, _b;
        (_a = this.room) === null || _a === void 0 ? void 0 : _a.on('reconnecting', error => {
            if (error.code === 53001) {
                logger_2.logger.warn('Reconnecting Twilio signaling connection', error.message);
            }
            else if (error.code === 53405) {
                logger_2.logger.warn('Reconnecting Twilio media connection', error.message);
            }
            else {
                logger_2.logger.warn(`Connectivity to Twilio room ${this.roomId} lost; attempting to reconnect:`, error.message);
            }
            this.connectionStatusSubject.next('RECONNECTING');
            logger_2.logger.info('Reconnecting to room');
        });
        (_b = this.room) === null || _b === void 0 ? void 0 : _b.on('reconnected', () => {
            logger_2.logger.info(`Successfully reconnected to Twilio room`, this.roomId);
            this.errorCodeSubject.next(undefined);
            this.connectionStatusSubject.next('CONNECTED');
            logger_2.logger.info('Reconnected to room');
        });
    }
    /**
     * Resets all subjects in TwilioRoom
     */
    resetState() {
        navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeListener);
        navigator.mediaDevices.ondevicechange = undefined;
        this.canReceiveVideo = false;
        this.connectionStatusSubject.next('DISCONNECTED');
        this.errorCodeSubject.next(undefined);
        this.recordingSubject.next(false);
        this.room = undefined;
        this.remoteCapabilitiesListener.unsubscribe();
        this.remoteConnectionSubject.next(false);
        this.remoteNetworkSubject.next(0);
        this.localVideoErrorSubject.next(undefined);
        this.localVideoSubject.next(undefined);
        this.localAudioErrorSubject.next(undefined);
        this.localAudioSubject.next(undefined);
        this.localDataErrorSubject.next(undefined);
        this.localDataSubject.next(undefined);
        this.localConnectionSubject.next(false);
        this.localNetworkSubject.next(0);
        this.remoteVideoErrorSubject.next(undefined);
        this.remoteVideoSubject.next(undefined);
        this.remoteAudioErrorSubject.next(undefined);
        this.remoteAudioSubject.next(undefined);
        this.remoteDataErrorSubject.next(undefined);
        this.remoteDataSubject.next(undefined);
        this.roomMonitor.disconnect();
        this.analyticsSubscription.unsubscribe();
        this.localDevicesSubject.next([]);
        logger_2.logger.removeContextValue(logger_1.contextValues.PERMISSIONS_STATE);
        logger_2.logger.removeContextValue(logger_1.contextValues.TWILIO_LOCAL_NTWRK_QUAL);
        logger_2.logger.removeContextValue(logger_1.contextValues.TWILIO_REMOTE_NTWRK_QUAL);
        logger_2.logger.removeContextValue(logger_1.contextValues.TWILIO_NETWORK_QUAL_STATS);
        logger_2.logger.removeContextValue(logger_1.contextValues.TWILIO_WEB_RTC_STATS);
    }
    getStats() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const stats = (yield ((_a = this.room) === null || _a === void 0 ? void 0 : _a.getStats()));
            return stats;
        });
    }
    logStats(statsReport, roomMonitorStats) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const { localVideoTrackStats, localAudioTrackStats, remoteVideoTrackStats, remoteAudioTrackStats, } = statsReport;
            const compositeMediaStats = {
                localVideo: Object.assign(Object.assign({}, (localVideoTrackStats[0] && localVideoTrackStats[0])), (localVideoTrackStats[0] && {
                    bitRateKbps: (_a = roomMonitorStats.localVideoTrackStats) === null || _a === void 0 ? void 0 : _a.bitRate,
                    enabled: this._localVideoTrackEnabled,
                })),
                localAudio: Object.assign(Object.assign({}, (localAudioTrackStats[0] && localAudioTrackStats[0])), (localAudioTrackStats[0] && {
                    bitRateKbps: (_b = roomMonitorStats.localAudioTrackStats) === null || _b === void 0 ? void 0 : _b.bitRate,
                    enabled: this._localAudioTrackEnabled,
                })),
                remoteVideo: Object.assign(Object.assign({}, (remoteVideoTrackStats[0] && remoteVideoTrackStats[0])), (remoteVideoTrackStats[0] && {
                    bitRateKbps: (_c = roomMonitorStats.remoteVideoTrackStats) === null || _c === void 0 ? void 0 : _c.bitRate,
                })),
                remoteAudio: Object.assign(Object.assign({}, (remoteAudioTrackStats[0] && remoteAudioTrackStats[0])), (remoteAudioTrackStats[0] && {
                    bitRateKbps: (_d = roomMonitorStats.remoteAudioTrackStats) === null || _d === void 0 ? void 0 : _d.bitRate,
                })),
            };
            logger_2.logger.setContextValue(logger_1.contextValues.TWILIO_WEB_RTC_STATS, compositeMediaStats);
            logger_2.logger.info('Twilio WebRTC stats');
            logger_2.logger.removeContextValue(logger_1.contextValues.TWILIO_WEB_RTC_STATS);
            this.mediaWebRtcStatsSubject.next(statsReport);
            this.checkConnectionQuality();
        });
    }
    checkConnectionQuality() {
        const localConnection = this.room.localParticipant
            ? this.room.localParticipant.networkQualityLevel
            : undefined;
        let remoteConnection = undefined;
        const remoteConnections = Array.from(this.room.participants, ([_, participant]) => participant.networkQualityLevel);
        if (remoteConnections.length > 1) {
            logger_2.logger.debug('Found more then one remote participant connection; ignoring');
        }
        else if (remoteConnections.length === 1) {
            remoteConnection = remoteConnections[0];
        }
        if (localConnection && localConnection < 2) {
            logger_2.logger.setContextValue(logger_1.contextValues.TWILIO_LOCAL_NTWRK_QUAL, localConnection);
            logger_2.logger.warn('Twilio local participant network quality is low', localConnection);
        }
        if (remoteConnection && remoteConnection < 2) {
            logger_2.logger.setContextValue(logger_1.contextValues.TWILIO_REMOTE_NTWRK_QUAL, remoteConnection);
            logger_2.logger.warn('Twilio remote participant network quality is low', remoteConnection);
        }
    }
}
exports.TwilioRoom = TwilioRoom;
//# sourceMappingURL=twilio_room.js.map